import React, { useState, useEffect } from "react";
import { omit, mergeDeepRight } from "ramda";
import { useParams } from "react-router-dom";
import {
  useAllModuleContent,
  useAllModuleContentCount,
} from "../moduleServices";
import { useSearchQuery } from "../../common/use-searchQuery";
import { formatSort } from "../../../util/stringFormat";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { OrganizeProvider } from "../../organize/OrganizeProvider";

import ModuleFilesBrowser from "./ModuleFilesBrowser";

const initialSearchState = (initialState = {}) => {
  return mergeDeepRight(
    {
      appliedQuery: "",
      appliedFacets: [],
      page: 0,
      limit: 10,
      orderBy: "name",
      orderDirection: "asc",
    },
    initialState,
  );
};

const ModuleFilesTab = ({ module }) => {
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const SnackAlertHandler = useSnackAlert();
  const initialContentSearchState = initialSearchState();
  const contentQuery = useSearchQuery(initialContentSearchState);

  const [queryState, setQueryState] = useState({
    ...initialContentSearchState,
    offset: contentQuery.state.limit * contentQuery.state.page,
    sort: formatSort(contentQuery.state),
    q: contentQuery.state.appliedQuery,
  });

  useEffect(() => {
    const { limit, page, appliedQuery } = contentQuery.state;
    const defaultSort = formatSort({
      orderBy: initialContentSearchState.orderBy,
      orderDirection: initialContentSearchState.orderDirection,
    });
    const sort =
      contentQuery.state.orderBy === null
        ? defaultSort
        : formatSort(contentQuery.state);

    const query = omit(
      ["orderBy", "orderDirection", "page"],
      contentQuery.state,
    );
    setQueryState({
      ...query,
      sort,
      offset: limit * page,
      q: appliedQuery,
    });
  }, [
    contentQuery.state,
    initialContentSearchState.orderBy,
    initialContentSearchState.orderDirection,
  ]);

  const contentResults = useAllModuleContent(id, queryState);
  const countResults = useAllModuleContentCount(id, {
    q: queryState.appliedQuery,
  });

  const refetch = () => {
    contentResults.refetch();
    countResults.refetch();
  };

  useEffect(() => {
    setLoading(contentResults.isLoading);
  }, [contentResults.isLoading]);

  return (
    <OrganizeProvider>
      <ModuleFilesBrowser
        appliedQuery={contentQuery.state.appliedQuery}
        content={contentResults.isSuccess ? contentResults.data : []}
        handlePageChange={(event, newPage) =>
          contentQuery.handleUpdatePage(newPage)
        }
        handleQueryChange={contentQuery.handleUpdateQuery}
        handleSortChange={contentQuery.handleUpdateSort}
        loading={loading}
        module={module}
        onRowsPerPageChange={contentQuery.handleUpdateLimit}
        orderBy={contentQuery.state.orderBy}
        order={contentQuery.state.orderDirection}
        page={contentQuery.state.page}
        refetch={refetch}
        rowsPerPage={contentQuery.state.limit}
        searchState={queryState}
        setLoading={setLoading}
        SnackAlertHandler={SnackAlertHandler}
        total={countResults.isSuccess ? countResults.data.count : "-"}
      />
    </OrganizeProvider>
  );
};

export default ModuleFilesTab;
